/**
 * Interactive Features Section — Dawn Theme
 * File: assets/section-interactive-features.js
 *
 * Handles hover (desktop) and click/touch (mobile) interactions.
 * Manages active feature items and corresponding media visibility.
 * Follows Dawn's custom element / class-based pattern.
 */

(function () {
  'use strict';

  /**
   * A tiny media query helper — returns true if we're on a pointer-coarse device
   * (phones, tablets) where hover is unreliable.
   */
  function isTouchDevice() {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  }

  class InteractiveFeatures {
    /**
     * @param {HTMLElement} container  — the [data-section-id] wrapper element
     */
    constructor(container) {
      this.container = container;
      this.items = Array.from(container.querySelectorAll('.if-feature-item'));
      this.mediaItems = Array.from(container.querySelectorAll('.if-media-item'));

      if (this.items.length === 0) return;

      this._activeIndex = 0;
      this._hoverTimeout = null;

      this._bindEvents();
    }

    /** Attach all event listeners */
    _bindEvents() {
      this.items.forEach((item, index) => {
        // Desktop: hover intent with a tiny delay to avoid flickering
        item.addEventListener('mouseenter', () => {
          if (isTouchDevice()) return;
          clearTimeout(this._hoverTimeout);
          this._hoverTimeout = setTimeout(() => this._activate(index), 80);
        });

        item.addEventListener('mouseleave', () => {
          clearTimeout(this._hoverTimeout);
        });

        // Mobile + keyboard: click / tap
        item.addEventListener('click', () => this._activate(index));

        // Keyboard: Enter and Space
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._activate(index);
          }
        });
      });
    }

    /**
     * Activate a feature item by index.
     * @param {number} index
     */
    _activate(index) {
      if (index === this._activeIndex) return;

      // Deactivate previous
      this._deactivate(this._activeIndex);

      // Activate new
      const item = this.items[index];
      const media = this.mediaItems[index];

      if (item) {
        item.classList.add('if-feature-item--active');
        item.setAttribute('aria-selected', 'true');
      }

      if (media) {
        media.classList.add('if-media-item--active');
        media.setAttribute('aria-hidden', 'false');

        // Resume video if present
        const video = media.querySelector('video');
        if (video) {
          video.play().catch(() => {
            // Autoplay may be blocked; silently fail
          });
        }
      }

      this._activeIndex = index;
    }

    /**
     * Deactivate a feature item by index.
     * @param {number} index
     */
    _deactivate(index) {
      const item = this.items[index];
      const media = this.mediaItems[index];

      if (item) {
        item.classList.remove('if-feature-item--active');
        item.setAttribute('aria-selected', 'false');
      }

      if (media) {
        media.classList.remove('if-media-item--active');
        media.setAttribute('aria-hidden', 'true');

        // Pause video to save bandwidth
        const video = media.querySelector('video');
        if (video) video.pause();
      }
    }
  }

  /** Initialise all sections on the page */
  function init() {
    document.querySelectorAll('[data-section-id]').forEach((container) => {
      // Only init containers that are Interactive Feature sections
      if (container.querySelector('.if-feature-item')) {
        new InteractiveFeatures(container);
      }
    });
  }

  // Run on DOMContentLoaded or immediately if the DOM is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when Shopify injects a new section in the Theme Editor
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('[data-section-id]');
    if (container && container.querySelector('.if-feature-item')) {
      new InteractiveFeatures(container);
    }
  });
})();